// Conversation Detail Modal - Shows full Hope AI conversation with provider notes
import React, { useState, useEffect } from 'react';
import {
  X,
  Heart,
  Clock,
  MessageCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Save,
  User,
  Bot
} from 'lucide-react';
import { HopeConversationSummary, HopeMessage } from '../../types/activity';

interface ConversationDetailModalProps {
  conversation: HopeConversationSummary;
  onClose: () => void;
  onSaveNotes: (notes: string) => Promise<void>;
}

const ConversationDetailModal: React.FC<ConversationDetailModalProps> = ({
  conversation,
  onClose,
  onSaveNotes
}) => {
  const [providerNotes, setProviderNotes] = useState(conversation.providerNotes || '');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [showAllMessages, setShowAllMessages] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Format timestamp for messages
  const formatMessageTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format duration
  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Get sentiment color
  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return '#22c55e';
      case 'neutral':
        return '#94a3b8';
      case 'negative':
        return '#f97316';
      case 'concerning':
        return '#dc3545';
      default:
        return '#94a3b8';
    }
  };

  // Handle save notes
  const handleSaveNotes = async () => {
    setIsSavingNotes(true);
    try {
      await onSaveNotes(providerNotes);
    } catch (error) {
      console.error('Error saving notes:', error);
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Messages to display (limited or all)
  const displayMessages = showAllMessages
    ? conversation.messages
    : conversation.messages.slice(-10);

  const hasMoreMessages = conversation.messages.length > 10;

  return (
    <>
      {/* Backdrop */}
      <div
        style={styles.backdrop}
        onClick={onClose}
        role="presentation"
      />

      {/* Modal */}
      <div
        style={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.headerIcon}>
              <Heart size={24} color="#3b82f6" />
            </div>
            <div>
              <h2 id="modal-title" style={styles.title}>
                Hope Conversation
              </h2>
              <p style={styles.subtitle}>
                {conversation.clientName} •{' '}
                {conversation.startTime.toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={styles.closeButton}
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>

        {/* Stats Bar */}
        <div style={styles.statsBar}>
          <div style={styles.statItem}>
            <Clock size={16} color="#64748b" />
            <span>{formatDuration(conversation.duration)}</span>
          </div>
          <div style={styles.statItem}>
            <MessageCircle size={16} color="#64748b" />
            <span>{conversation.messageCount} messages</span>
          </div>
          <div
            style={{
              ...styles.statItem,
              color: getSentimentColor(conversation.sentiment)
            }}
          >
            <span
              style={{
                ...styles.sentimentDot,
                backgroundColor: getSentimentColor(conversation.sentiment)
              }}
            />
            <span style={{ textTransform: 'capitalize' }}>
              {conversation.sentiment}
            </span>
          </div>
          {conversation.crisisDetected && (
            <div style={styles.crisisIndicator}>
              <AlertTriangle size={16} />
              <span>Crisis Detected</span>
            </div>
          )}
        </div>

        {/* Keywords */}
        {conversation.keywords.length > 0 && (
          <div style={styles.keywordsSection}>
            <span style={styles.keywordsLabel}>Keywords mentioned:</span>
            <div style={styles.keywordsTags}>
              {conversation.keywords.map((keyword, index) => (
                <span key={index} style={styles.keywordTag}>
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        <div style={styles.messagesSection}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>Conversation</h3>
            {hasMoreMessages && !showAllMessages && (
              <button
                onClick={() => setShowAllMessages(true)}
                style={styles.showMoreButton}
              >
                <ChevronUp size={16} />
                Show all {conversation.messages.length} messages
              </button>
            )}
          </div>

          <div style={styles.messagesContainer}>
            {!showAllMessages && hasMoreMessages && (
              <div style={styles.moreMessagesNote}>
                {conversation.messages.length - 10} earlier messages not shown
              </div>
            )}

            {displayMessages.map((message, index) => (
              <div
                key={message.id || index}
                style={{
                  ...styles.messageRow,
                  justifyContent:
                    message.sender === 'client' ? 'flex-end' : 'flex-start'
                }}
              >
                {message.sender === 'hope' && (
                  <div style={styles.avatarHope}>
                    <Bot size={16} color="#fff" />
                  </div>
                )}

                <div
                  style={{
                    ...styles.messageBubble,
                    ...(message.sender === 'client'
                      ? styles.messageClient
                      : styles.messageHope),
                    ...(message.flagged ? styles.messageFlagged : {})
                  }}
                >
                  <div style={styles.messageContent}>{message.content}</div>
                  <div style={styles.messageTime}>
                    {formatMessageTime(message.timestamp)}
                    {message.flagged && (
                      <span style={styles.flagIcon}>
                        <AlertTriangle size={12} />
                      </span>
                    )}
                  </div>
                </div>

                {message.sender === 'client' && (
                  <div style={styles.avatarClient}>
                    <User size={16} color="#fff" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Provider Notes */}
        <div style={styles.notesSection}>
          <h3 style={styles.sectionTitle}>Provider Notes</h3>
          <textarea
            value={providerNotes}
            onChange={(e) => setProviderNotes(e.target.value)}
            placeholder="Add notes about this conversation..."
            style={styles.notesTextarea}
            rows={4}
          />
          <div style={styles.notesFooter}>
            {conversation.reviewedAt && conversation.reviewedBy && (
              <span style={styles.reviewedInfo}>
                Last reviewed by {conversation.reviewedBy} on{' '}
                {conversation.reviewedAt.toLocaleDateString()}
              </span>
            )}
            <button
              onClick={handleSaveNotes}
              disabled={isSavingNotes}
              style={{
                ...styles.saveButton,
                ...(isSavingNotes ? styles.saveButtonDisabled : {})
              }}
            >
              <Save size={16} />
              {isSavingNotes ? 'Saving...' : 'Save Notes'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 1000
  },
  modal: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '700px',
    maxHeight: '90vh',
    backgroundColor: '#0f172a',
    borderRadius: '16px',
    border: '1px solid #334155',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 1001
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #334155'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px'
  },
  headerIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#f8fafc'
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '13px',
    color: '#64748b'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    padding: '8px',
    cursor: 'pointer',
    color: '#64748b',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  statsBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    padding: '14px 24px',
    backgroundColor: '#1e293b',
    borderBottom: '1px solid #334155'
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#94a3b8'
  },
  sentimentDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%'
  },
  crisisIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#dc3545',
    marginLeft: 'auto'
  },
  keywordsSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 24px',
    borderBottom: '1px solid #334155',
    flexWrap: 'wrap'
  },
  keywordsLabel: {
    fontSize: '12px',
    color: '#64748b'
  },
  keywordsTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },
  keywordTag: {
    fontSize: '11px',
    padding: '4px 10px',
    borderRadius: '12px',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    color: '#3b82f6'
  },
  messagesSection: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 24px 10px'
  },
  sectionTitle: {
    margin: 0,
    fontSize: '14px',
    fontWeight: '600',
    color: '#f8fafc'
  },
  showMoreButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: 'none',
    border: 'none',
    padding: '4px 8px',
    fontSize: '12px',
    color: '#3b82f6',
    cursor: 'pointer'
  },
  messagesContainer: {
    flex: 1,
    overflow: 'auto',
    padding: '0 24px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  moreMessagesNote: {
    textAlign: 'center',
    fontSize: '12px',
    color: '#64748b',
    padding: '8px',
    backgroundColor: '#1e293b',
    borderRadius: '8px',
    marginBottom: '8px'
  },
  messageRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px'
  },
  avatarHope: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  avatarClient: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: '#8b5cf6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  messageBubble: {
    maxWidth: '75%',
    padding: '10px 14px',
    borderRadius: '12px'
  },
  messageHope: {
    backgroundColor: '#1e293b',
    borderBottomLeftRadius: '4px'
  },
  messageClient: {
    backgroundColor: '#3b82f6',
    borderBottomRightRadius: '4px'
  },
  messageFlagged: {
    border: '1px solid rgba(220, 53, 69, 0.5)'
  },
  messageContent: {
    fontSize: '14px',
    color: '#f8fafc',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  messageTime: {
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.5)',
    marginTop: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  flagIcon: {
    color: '#dc3545'
  },
  notesSection: {
    padding: '16px 24px',
    borderTop: '1px solid #334155',
    backgroundColor: '#1e293b'
  },
  notesTextarea: {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #334155',
    backgroundColor: '#0f172a',
    color: '#f8fafc',
    fontSize: '14px',
    resize: 'vertical',
    fontFamily: 'inherit',
    marginTop: '10px'
  },
  notesFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '12px'
  },
  reviewedInfo: {
    fontSize: '12px',
    color: '#64748b'
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  saveButtonDisabled: {
    backgroundColor: '#334155',
    cursor: 'not-allowed'
  }
};

export default ConversationDetailModal;
